import Phaser from "phaser";
import { DESIGN_WIDTH, DESIGN_HEIGHT, COLORS, LAYOUT, TEXT_STYLES } from "../constants";
import { getCharacterAvatar } from "../avatars/characterAvatars";
import { PrimitiveAvatar } from "../avatars/PrimitiveAvatar";
import { VirtualJoystick } from "../ui/VirtualJoystick";
import { OverlayManager } from "../ui/OverlayManager";
import { OverworldState, OverworldZoneState, newOverworldState } from "../state/overworldState";
import { ENCOUNTER_SCENE_KEY } from "./EncounterScene";
import { getAutomationOverride } from "../automation/automationTags";
import { RUN_CHANGED_EVENT, RUN_HUD_SCENE_KEY, RUN_QUIT_EVENT } from "./RunHudScene";
import { MAIN_MENU_SCENE_KEY } from "./MainMenuScene";
import {
  checkOverworldZoneEntry,
  ensureOverworldMap,
  OverworldNavigationRequest,
  OverworldBounds,
} from "../state/overworldLogic";
import { navLog, navWarn } from "../debugNavigation";

export const OVERWORLD_SCENE_KEY = "OVERWORLD";

// World bounds - full design size
const WORLD_WIDTH = DESIGN_WIDTH;
const WORLD_HEIGHT = DESIGN_HEIGHT;
const WALK_SPEED = 180; // pixels per second
const PLAYER_RADIUS = 20; // Approximate player collision radius
const ZONE_VISUAL_RADIUS = 55;

// Player spawn point (can be moved later if needed)
export const PLAYER_SPAWN_X = WORLD_WIDTH / 2;
export const PLAYER_SPAWN_Y = WORLD_HEIGHT / 2;

// Obstacle definitions (rectangles)
interface Obstacle {
  x: number;
  y: number;
  width: number;
  height: number;
}

const OBSTACLES: Obstacle[] = [
  { x: 24, y: 140, width: 60, height: 80 },
  { x: 456, y: 300, width: 60, height: 80 },
  { x: 40, y: 760, width: 70, height: 90 },
];

type OverworldSceneData = {
  overworldState?: OverworldState;
  /**
   * @deprecated Navigation should be executed by the active scene (OverworldScene).
   * Passing a callback here can capture stale Scene instances and break transitions.
   */
  onNavigate?: (navigation: OverworldNavigationRequest) => void;
};

export class OverworldScene extends Phaser.Scene {
  private playerAvatar!: PrimitiveAvatar;
  private joystick!: VirtualJoystick;
  private overlayLayer!: Phaser.GameObjects.Container;
  private overlay!: OverlayManager;
  private playerX = WORLD_WIDTH / 2;
  private playerY = WORLD_HEIGHT / 2;
  private movementVector = { x: 0, y: 0 };
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasdKeys!: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
  };
  private overworldState!: OverworldState;
  private encounterZones: OverworldZoneState[] = [];
  private zoneGraphics: Phaser.GameObjects.Graphics[] = [];
  private zoneIndicators: Phaser.GameObjects.GameObject[] = [];
  private obstacles: Obstacle[] = [];
  private obstacleGraphics: Phaser.GameObjects.Rectangle[] = [];
  private debugHitboxes = false;
  private debugGraphics?: Phaser.GameObjects.Graphics;
  private unsubscribeQuit?: () => void;

  constructor() {
    super(OVERWORLD_SCENE_KEY);
  }

  /**
   * OverworldScene is the dedicated "overworld exploration" game mode.
   * It owns player movement, collisions, encounter-zone detection, and camera
   * control. Only this scene should manage overworld input/routing; scene swaps
   * should be reserved for switching to EncounterScene (and back) when the game
   * mode changes.
   */
  create(data?: OverworldSceneData) {
    // Check for debug flags
    const params = new URLSearchParams(window.location.search);
    this.debugHitboxes = params.has("debugHitboxes") || !!getAutomationOverride("debugHitboxes");

    // Create overlay layer and manager
    this.overlayLayer = this.add.container(0, 0);
    this.overlayLayer.setDepth(2000);
    this.overlayLayer.setScrollFactor(0);
    this.overlay = new OverlayManager(this, this.overlayLayer);

    // Ensure persistent run HUD is active and on top.
    if (!this.scene.isActive(RUN_HUD_SCENE_KEY)) {
      this.scene.launch(RUN_HUD_SCENE_KEY);
    }
    this.scene.bringToTop(RUN_HUD_SCENE_KEY);

    // Load or create overworld state
    this.overworldState = data?.overworldState ?? newOverworldState();
    if (data?.onNavigate) {
      navWarn(
        "OverworldScene",
        "Ignoring deprecated onNavigate callback (prevents stale scene refs)."
      );
    }

    const bounds: OverworldBounds = { width: WORLD_WIDTH, height: WORLD_HEIGHT };
    ensureOverworldMap(this.overworldState, bounds);
    // Use saved position if available, otherwise use spawn point
    this.playerX = this.overworldState.playerX || PLAYER_SPAWN_X;
    this.playerY = this.overworldState.playerY || PLAYER_SPAWN_Y;
    // Update state with initial position if it wasn't set
    if (!this.overworldState.playerX && !this.overworldState.playerY) {
      this.overworldState.playerX = this.playerX;
      this.overworldState.playerY = this.playerY;
    }

    // Create a simple background (placeholder grid)
    this.createBackground();

    // Create obstacles
    this.createObstacles();

    // Create encounter zones
    this.createEncounterZones();

    // Create player avatar based on character ID
    const playerBlueprint = getCharacterAvatar(this.overworldState.run.characterId);
    this.playerAvatar = new PrimitiveAvatar(this, playerBlueprint, {
      scale: 0.3,
    });
    // Position avatar with container origin offset (top-left to center)
    const avatarSize = this.playerAvatar.getSize();
    this.playerAvatar.setPosition(
      this.playerX - avatarSize.width / 2,
      this.playerY - avatarSize.height / 2
    );

    // Set up camera to follow player
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.startFollow(this.playerAvatar.getRoot(), true, 0.1, 0.1);
    this.cameras.main.setZoom(1.0);

    // Create virtual joystick
    this.joystick = new VirtualJoystick(this, {
      x: LAYOUT.screenPadding + 50,
      y: DESIGN_HEIGHT - LAYOUT.screenPadding - 50,
    });

    // Set up keyboard controls for desktop testing
    this.setupKeyboardControls();

    // Publish current run state to the persistent HUD.
    // Delay slightly to ensure HUD scene has finished creating and registered its event listener.
    this.time.delayedCall(0, () => {
      this.game.events.emit(RUN_CHANGED_EVENT, this.overworldState.run);
    });

    // Handle quit requests initiated from the HUD.
    const onQuit = () => {
      this.overlay.transition(() => {
        if (this.scene.isActive(RUN_HUD_SCENE_KEY)) {
          this.scene.stop(RUN_HUD_SCENE_KEY);
        }
        this.scene.start(MAIN_MENU_SCENE_KEY);
      });
    };
    this.game.events.on(RUN_QUIT_EVENT, onQuit);
    this.unsubscribeQuit = () => this.game.events.off(RUN_QUIT_EVENT, onQuit);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribeQuit?.());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.unsubscribeQuit?.());

    // Create debug graphics layer if enabled
    if (this.debugHitboxes) {
      this.debugGraphics = this.add.graphics();
      this.debugGraphics.setDepth(999);
    }

    // Listen to joystick movement
    this.joystick.onMove((vector) => {
      // Validate vector values before updating
      if (vector && isFinite(vector.x) && isFinite(vector.y)) {
        this.movementVector = {
          x: Phaser.Math.Clamp(vector.x, -1, 1),
          y: Phaser.Math.Clamp(vector.y, -1, 1),
        };
      } else {
        this.movementVector = { x: 0, y: 0 };
      }
    });
  }

  private createBackground() {
    // Simple colored background with a subtle grid pattern
    this.add.rectangle(
      WORLD_WIDTH / 2,
      WORLD_HEIGHT / 2,
      WORLD_WIDTH,
      WORLD_HEIGHT,
      COLORS.background
    );

    // Add a subtle grid pattern
    const gridColor = Phaser.Display.Color.ValueToColor(COLORS.panelFill).color;
    const gridAlpha = 0.15;
    const gridSize = 40;

    const graphics = this.add.graphics();
    graphics.lineStyle(1, gridColor, gridAlpha);

    // Vertical lines
    for (let x = 0; x <= WORLD_WIDTH; x += gridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, WORLD_HEIGHT);
    }

    // Horizontal lines
    for (let y = 0; y <= WORLD_HEIGHT; y += gridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(WORLD_WIDTH, y);
    }

    graphics.strokePath();
  }

  private createObstacles() {
    this.obstacles = [...OBSTACLES];
    this.obstacleGraphics = [];

    this.obstacles.forEach((obstacle) => {
      const rect = this.add.rectangle(
        obstacle.x + obstacle.width / 2,
        obstacle.y + obstacle.height / 2,
        obstacle.width,
        obstacle.height,
        COLORS.panelFill,
        0.6
      );
      rect.setStrokeStyle(2, COLORS.panelStroke);
      this.obstacleGraphics.push(rect);
    });
  }

  private createEncounterZones() {
    this.zoneGraphics.forEach((graphic) => graphic.destroy());
    this.zoneIndicators.forEach((indicator) => indicator.destroy());
    this.zoneGraphics = [];
    this.zoneIndicators = [];

    this.encounterZones = this.overworldState.zones;

    const healColor = Phaser.Display.Color.HexStringToColor(COLORS.healText).color;
    const damageColor = Phaser.Display.Color.HexStringToColor(COLORS.damageText).color;

    this.encounterZones.forEach((zone) => {
      const isLocked = zone.status === "locked";
      const isCompleted = zone.status === "completed";
      const visualRadius = ZONE_VISUAL_RADIUS;

      // Zone-specific styling
      let fillColor = COLORS.buttonFill;
      let strokeColor = COLORS.buttonStroke;
      let strokeWidth = 3;

      if (zone.kind === "boss") {
        strokeColor = COLORS.skillBadgeFill;
        strokeWidth = 4;
      } else if (zone.kind === "rest") {
        strokeColor = healColor;
        fillColor = COLORS.panelFill;
      }

      const alpha = isLocked ? 0.08 : isCompleted ? 0.06 : 0.20;
      const strokeAlpha = isLocked ? 0.25 : 0.6;

      const graphics = this.add.graphics();
      graphics.lineStyle(strokeWidth, strokeColor, strokeAlpha);
      graphics.fillStyle(fillColor, alpha);
      graphics.fillCircle(zone.x, zone.y, visualRadius);
      graphics.strokeCircle(zone.x, zone.y, visualRadius);
      this.zoneGraphics.push(graphics);

      // Show status indicator for completed/locked zones
      if (isCompleted || isLocked) {
        const indicatorBg = this.add.circle(zone.x, zone.y, 18, COLORS.panelFill, 0.9);
        indicatorBg.setStrokeStyle(2, strokeColor, 0.8);
        const label = isCompleted ? "✓" : "🔒";
        const indicatorText = this.add.text(zone.x, zone.y, label, {
          ...TEXT_STYLES.subtitle,
          fontSize: "20px",
          color: COLORS.textPrimary,
          align: "center",
        });
        indicatorText.setOrigin(0.5, 0.5);
        indicatorBg.setDepth(2);
        indicatorText.setDepth(3);
        this.zoneIndicators.push(indicatorBg, indicatorText);
      }

      // Show zone type icon for available zones
      if (!isCompleted && !isLocked) {
        const icon = this.getZoneIcon(zone);
        const iconText = this.add.text(zone.x, zone.y, icon, {
          ...TEXT_STYLES.subtitle,
          fontSize: "24px",
        });
        iconText.setOrigin(0.5, 0.5);
        iconText.setDepth(2);
        this.zoneIndicators.push(iconText);

        // Enemy count badge for combat zones
        if (zone.kind === "encounter" || zone.kind === "boss") {
          const enemyCount = zone.enemyConfig?.enemies?.length ?? 1;
          const badgeX = zone.x + visualRadius * 0.6;
          const badgeY = zone.y - visualRadius * 0.6;

          const countBadge = this.add.circle(badgeX, badgeY, 12, damageColor, 0.9);
          countBadge.setStrokeStyle(2, COLORS.panelStroke, 0.9);
          countBadge.setDepth(2);

          const countText = this.add.text(badgeX, badgeY, `${enemyCount}`, {
            ...TEXT_STYLES.body,
            fontSize: "14px",
            color: COLORS.textPrimary,
          });
          countText.setOrigin(0.5, 0.5);
          countText.setDepth(3);

          this.zoneIndicators.push(countBadge, countText);
        }
      }
    });
  }

  private getZoneIcon(zone: { kind: string }): string {
    if (zone.kind === "boss") return "👑";
    if (zone.kind === "rest") return "🔥";
    return "⚔"; // Regular encounter
  }

  private setupKeyboardControls() {
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasdKeys = this.input.keyboard!.addKeys("W,A,S,D") as {
      W: Phaser.Input.Keyboard.Key;
      A: Phaser.Input.Keyboard.Key;
      S: Phaser.Input.Keyboard.Key;
      D: Phaser.Input.Keyboard.Key;
    };
  }

  update(_time: number, delta: number) {
    // Update joystick for smooth following behavior
    this.joystick?.update();

    // Update movement vector from keyboard if joystick is not active
    if (this.joystick && !this.joystick.isActive()) {
      this.updateKeyboardMovement();
    }

    // Validate movement vector
    if (!isFinite(this.movementVector.x)) {
      this.movementVector.x = 0;
    }
    if (!isFinite(this.movementVector.y)) {
      this.movementVector.y = 0;
    }

    // Apply movement with collision detection
    const speed = (WALK_SPEED * delta) / 1000;
    const moveX = this.movementVector.x * speed;
    const moveY = this.movementVector.y * speed;

    // Try to move
    const newX = this.playerX + moveX;
    const newY = this.playerY + moveY;

    // Check collision with obstacles
    const canMoveX = !this.checkObstacleCollision(newX, this.playerY);
    const canMoveY = !this.checkObstacleCollision(this.playerX, newY);

    // Apply movement if no collision
    if (isFinite(moveX) && isFinite(moveY)) {
      if (canMoveX) {
        this.playerX = newX;
      }
      if (canMoveY) {
        this.playerY = newY;
      }
    }

    // Clamp player position within world bounds
    const avatarSize = this.playerAvatar.getSize();
    const halfWidth = avatarSize.width / 2;
    const halfHeight = avatarSize.height / 2;

    this.playerX = Phaser.Math.Clamp(this.playerX, halfWidth, WORLD_WIDTH - halfWidth);
    this.playerY = Phaser.Math.Clamp(this.playerY, halfHeight, WORLD_HEIGHT - halfHeight);

    // Update avatar position
    // Container origin is at (0,0) (top-left), so offset by half size to center it
    this.playerAvatar.setPosition(this.playerX - halfWidth, this.playerY - halfHeight);

    const root = this.playerAvatar.getRoot();
    if (root) {
      const baseScaleX = Math.abs(root.scaleX) || 1;
      if (root.scaleX !== baseScaleX) {
        root.setScale(baseScaleX, root.scaleY);
      }
    }

    // Check for encounter zone collision
    this.checkEncounterZones();

    // Draw debug hitboxes if enabled
    if (this.debugHitboxes && this.debugGraphics) {
      this.drawDebugHitboxes();
    }
  }

  private checkObstacleCollision(x: number, y: number): boolean {
    const playerRect = {
      left: x - PLAYER_RADIUS,
      right: x + PLAYER_RADIUS,
      top: y - PLAYER_RADIUS,
      bottom: y + PLAYER_RADIUS,
    };

    for (const obstacle of this.obstacles) {
      if (
        playerRect.right > obstacle.x &&
        playerRect.left < obstacle.x + obstacle.width &&
        playerRect.bottom > obstacle.y &&
        playerRect.top < obstacle.y + obstacle.height
      ) {
        return true;
      }
    }
    return false;
  }

  private checkEncounterZones() {
    const entry = checkOverworldZoneEntry(
      this.overworldState,
      { x: this.playerX, y: this.playerY },
      PLAYER_RADIUS
    );
    if (!entry) {
      return;
    }

    const { zone, navigation } = entry;
    navLog("OverworldScene", "Zone entered; preparing encounter.", {
      zoneId: zone.id,
      zoneKind: zone.kind,
      zoneStatus: zone.status,
      playerX: Math.round(this.playerX),
      playerY: Math.round(this.playerY),
      floor: this.overworldState.run.floor,
    });

    // Handle rest zone navigation
    if (navigation.kind === "rest") {
      navLog("OverworldScene", "Starting ENCOUNTER scene for rest.", {
        zoneId: zone.id,
      });

      this.overlay.transition(() => {
        this.scene.start(ENCOUNTER_SCENE_KEY, {
          fromOverworld: true,
          overworldState: navigation.overworldState,
          targetScreen: "REST",
        });
      });
      return;
    }

    // Handle combat encounter navigation
    const activeScenes = this.scene.manager
      .getScenes(true)
      .map((scene) => scene.scene.key)
      .join(", ");

    navLog("OverworldScene", "Starting ENCOUNTER scene for encounter.", {
      activeScenes,
      isBossEncounter: navigation.isBossEncounter,
      enemyCount: navigation.enemyConfig?.enemies.length ?? 0,
      zoneId: zone.id,
    });

    // Always navigate using the active scene manager (avoid stale scene refs).
    // Wrap in overlay transition for consistent scene swaps.
    this.overlay.transition(() => {
      this.scene.start(ENCOUNTER_SCENE_KEY, {
        fromOverworld: true,
        overworldState: navigation.overworldState,
        enemyConfig: navigation.enemyConfig,
      });
    });
  }

  private updateKeyboardMovement() {
    let x = 0;
    let y = 0;

    // Check arrow keys or WASD
    if (this.cursors.left?.isDown || this.wasdKeys.A.isDown) {
      x -= 1;
    }
    if (this.cursors.right?.isDown || this.wasdKeys.D.isDown) {
      x += 1;
    }
    if (this.cursors.up?.isDown || this.wasdKeys.W.isDown) {
      y -= 1;
    }
    if (this.cursors.down?.isDown || this.wasdKeys.S.isDown) {
      y += 1;
    }

    // Normalize diagonal movement
    if (x !== 0 && y !== 0) {
      const length = Math.sqrt(x * x + y * y);
      x /= length;
      y /= length;
    }

    this.movementVector = { x, y };
  }

  private drawDebugHitboxes() {
    if (!this.debugGraphics) return;

    this.debugGraphics.clear();

    // Draw player collision circle (cyan)
    this.debugGraphics.lineStyle(2, 0x00ffff, 1);
    this.debugGraphics.strokeCircle(this.playerX, this.playerY, PLAYER_RADIUS);

    // Draw player center point (red dot)
    this.debugGraphics.fillStyle(0xff0000, 1);
    this.debugGraphics.fillCircle(this.playerX, this.playerY, 3);

    // Draw obstacle collision boxes (yellow)
    this.debugGraphics.lineStyle(2, 0xffff00, 0.8);
    for (const obstacle of this.obstacles) {
      this.debugGraphics.strokeRect(obstacle.x, obstacle.y, obstacle.width, obstacle.height);
    }

    // Draw encounter zone collision circles (green)
    this.debugGraphics.lineStyle(2, 0x00ff00, 0.8);
    for (const zone of this.encounterZones) {
      this.debugGraphics.strokeCircle(zone.x, zone.y, zone.radius + PLAYER_RADIUS);
      // Draw zone center (green dot)
      this.debugGraphics.fillStyle(0x00ff00, 1);
      this.debugGraphics.fillCircle(zone.x, zone.y, 3);
    }
  }
}
